import { scmpPlus } from "@product/scmp-sdk";
import { addMonths, endOfDay, startOfDay } from "date-fns";
import { useEffect, useRef, useState } from "react";
import type { FunctionComponent, ReactNode } from "react";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";

import { Spacer } from "~/components/common/spacer";
import type { DateRangePair } from "~/components/plus/date-picker/types";
import { useNewsAgendaContext } from "~/components/plus/news-agenda/contexts";
import { data as appData } from "~/data";
import { useInfiniteScrollTriggerReference } from "~/lib/hooks";
import type { filterAgendaFilterContent$key } from "~/queries/__generated__/filterAgendaFilterContent.graphql";
import type { filterAgendaFilterQuery } from "~/queries/__generated__/filterAgendaFilterQuery.graphql";
import type { hooksNewsAgendaFilter$data } from "~/queries/__generated__/hooksNewsAgendaFilter.graphql";

import { useNewsAgendaFilter } from "./hooks";
import {
  Container,
  StyledCheckbox,
  StyledDatePicker,
  StyledFormControlLabel,
  StyledLabel,
} from "./styles";

type ChildProps = {
  hasNextPage: boolean;
  infiniteScrollTriggerReference: (dom: HTMLElement | null) => void;
  selected: string | null;
  topics: hooksNewsAgendaFilter$data;
};

type Props = {
  children?: (props: ChildProps) => ReactNode;
  className?: string;
  entityId?: string | undefined;
  isDefaultCheckedKeyEvents?: boolean;
  startDate?: Date | undefined;
  withDatePicker?: boolean;
  withKeyEvent?: boolean;
};

const agendaPageSize = 25;

export const AgendaFilter: FunctionComponent<Props> = ({
  children,
  className,
  entityId,
  isDefaultCheckedKeyEvents = false,
  startDate,
  withDatePicker = true,
  withKeyEvent = true,
}) => {
  const [onlyKeyEvents, setOnlyKeyEvents] = useState(isDefaultCheckedKeyEvents);
  const startDateValue = startOfDay(startDate ?? new Date());
  const endDateValue = endOfDay(addMonths(startDateValue, 1));
  const defaultStartDate = useRef(startDateValue);
  const defaultEndDate = useRef(endDateValue);
  const [dateRange, setDateRange] = useState<DateRangePair>([
    defaultStartDate.current,
    defaultEndDate.current,
  ]);

  const data = useLazyLoadQuery<filterAgendaFilterQuery>(
    graphql`
      query filterAgendaFilterQuery(
        $applicationId: String!
        $startDate: Timestamp!
        $endDate: Timestamp!
        $types: [String]!
        $newsAgendaOptions: [String!]
      ) {
        ...filterAgendaFilterContent
          @arguments(
            applicationId: $applicationId
            startDate: $startDate
            endDate: $endDate
            types: $types
            newsAgendaOptions: $newsAgendaOptions
          )
      }
    `,
    {
      applicationId: appData.application.scmp.entityId,
      endDate: dateRange?.[1]?.getTime() ?? defaultEndDate.current.getTime(),
      newsAgendaOptions: onlyKeyEvents ? ["key_event"] : [],
      startDate: dateRange?.[0]?.getTime() ?? defaultStartDate.current.getTime(),
      types: [scmpPlus.topicType.newsAgenda],
    },
    {
      fetchPolicy: "store-and-network",
    },
  );

  const {
    data: topicList,
    hasNext: hasNextPage,
    isLoadingNext: isLoading,
    loadNext,
  } = usePaginationFragment<filterAgendaFilterQuery, filterAgendaFilterContent$key>(
    graphql`
      fragment filterAgendaFilterContent on Query
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int", defaultValue: 25 }
        applicationId: { type: "String!" }
        startDate: { type: "Timestamp!" }
        endDate: { type: "Timestamp!" }
        types: { type: "[String]!" }
        newsAgendaOptions: { type: "[String!]" }
      )
      @refetchable(queryName: "filterAgendaFilterPaginationQuery") {
        topics(
          after: $after
          first: $count
          applicationId: $applicationId
          filter: {
            types: $types
            newsAgendaDateRange: { startDate: $startDate, endDate: $endDate }
            newsAgendaOptions: $newsAgendaOptions
          }
          orderBy: { field: NEWS_AGENDA_START_DATE, direction: ASC }
        ) @connection(key: "agendaFilter__topics") {
          edges {
            ...hooksNewsAgendaFilter
          }
        }
      }
    `,
    data,
  );

  const { filtered, selected } = useNewsAgendaFilter(topicList.topics.edges, entityId);
  const { setCurrentAgendaId, setShowEmptyAgenda } = useNewsAgendaContext();
  useEffect(() => {
    if (selected) {
      setShowEmptyAgenda?.(false);
      setCurrentAgendaId?.(selected);
    } else {
      setShowEmptyAgenda?.(true);
      setCurrentAgendaId?.("");
    }
  }, [selected, setShowEmptyAgenda, setCurrentAgendaId]);

  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage,
    isLoading,
    onLoadMore: () => loadNext(agendaPageSize),
  });

  return (
    <Container className={className}>
      <StyledDatePicker
        $isShow={withDatePicker}
        dateRange={dateRange}
        defaultDateRange={[defaultStartDate.current, defaultEndDate.current]}
        onChange={range => setDateRange(range)}
      />
      <Spacer />
      <StyledFormControlLabel
        $isShow={withKeyEvent}
        control={
          <StyledCheckbox
            checked={onlyKeyEvents}
            disableRipple
            onChange={event => setOnlyKeyEvents(event.target.checked)}
          />
        }
        label={<StyledLabel>Key events</StyledLabel>}
      />
      {children?.({ hasNextPage, infiniteScrollTriggerReference, selected, topics: filtered })}
    </Container>
  );
};

AgendaFilter.displayName = "AgendaFilter";
